import React, { type FunctionComponent } from 'react'
import { GenericChart } from '@retailer-platform/shared-components'
import { type DashStyleValue, type SeriesOptionsType } from 'highcharts'
import { type TrendData } from 'domains/insights-portal/utils/trendGraphs.utils'
import { useDomainMessages } from 'domains/insights-portal/utils/domain/intl'
import { trackEvent } from 'utils/events/trackEvent'
import { type AnalyticTrackingData } from 'domains/insights-portal/utils/insights-portal.types'
import { type transformMultiGroupDataToBenchmarkTrendData } from 'domains/insights-portal/widgets/KPIOverviewTrend/KpiOverviewTrend.utils'
import { InsightsPortalKpiComparisonTooltip } from './InsightsPortalKpiComparisonTooltip'

interface ComparisonSeries {
  data: TrendData['comparisonData']
  color?: string
  seriesName?: string
  dashType?: DashStyleValue
  primaryDataFillColor?: string | null
}

interface InsightsPortalTrendGraphProps {
  primaryData: TrendData['primaryData']
  comparisonData: TrendData['comparisonData']
  benchmarkData?: ReturnType<typeof transformMultiGroupDataToBenchmarkTrendData>
  metricName: string
  metricFormatter: (v: number) => string
  primarySeriesName?: string
  comparisonSeriesName?: string
  title?: string
  xAxisType?: 'date' | 'index'
  primaryColor?: string
  comparisonColor?: string
  showExportMenu?: boolean
  xAxisName?: string
  analyticTrackingData?: AnalyticTrackingData
  showAdditionalSeriesTrendLines?: boolean
  containerStyle?: React.CSSProperties
  additionalSeries: ComparisonSeries[]
}

export const InsightsPortalTrendGraph: FunctionComponent<InsightsPortalTrendGraphProps> = ({
  primaryData,
  comparisonData,
  benchmarkData,
  metricName,
  primarySeriesName = metricName,
  title,
  metricFormatter,
  xAxisType = 'date',
  primaryColor,
  showExportMenu = true,
  xAxisName = '',
  analyticTrackingData,
  additionalSeries = [],
  showAdditionalSeriesTrendLines = true,
  containerStyle,
}) => {
  const messages = useDomainMessages({
    comparisonPeriod: 'insightsPortalDomain.outOfStockDashboard.compareToValue.comparisonPeriod',
    benchmark: 'insightsPortalDomain.benchmark',
    comparedPreviousPeriod:
      'insightsPortalDomain.performanceDashboard.tooltips.comparedToPreviousPeriod',
  })

  return (
    <GenericChart
      seriesName={primarySeriesName}
      data={primaryData}
      graphType="line"
      title={title}
      containerStyle={containerStyle}
      formatter={metricFormatter}
      showInLegend
      color={primaryColor}
      showExportMenu={showExportMenu}
      showYAxisBothSides
      downloadCSVSideEffect={() => {
        trackEvent({
          id: `insights_portal.individual_chart_download.trigger`,
          description: `Downloaded an individual chart`,
          data: {
            chartName: 'Overview Performance',
            dashboardName: analyticTrackingData?.dashboard,
          },
        })
      }}
      truncateYAxis={true}
      optionProps={{
        xAxis:
          xAxisType === 'date'
            ? {
                type: 'datetime',
                title: {
                  text: xAxisName,
                },
                ordinal: false,
                labels: {
                  format: '{value:%b %e, %Y}',
                },
              }
            : {
                labels: {
                  format: '{value}',
                },
                type: 'linear',
                title: {
                  text: xAxisName,
                },
              },
        plotOptions: {
          series: {
            dataLabels: {
              enabled: false,
            },
          },
        },
        tooltip: {
          shared: true,
          useHTML: true,
        },
        series: additionalSeries.map(series => {
          const commonProperties: SeriesOptionsType = {
            type: series.primaryDataFillColor ? 'area' : 'line',
            dashStyle: series.dashType || 'Dash',
            name: series.seriesName || messages.comparisonPeriod,
            data: series.data,
            color: series.color,
            fillColor: {
              linearGradient: { x1: 0, y1: 0, x2: 0, y2: 1 },
              stops: [
                [0, series.primaryDataFillColor],
                [1, 'rgba(247, 247, 247, 0.28)'],
              ],
            },
            label: { enabled: false },
          }
          const optionalProperties = showAdditionalSeriesTrendLines
            ? {}
            : {
                showInLegend: false,

                lineWidth: 0,
                lineColor: 'transparent',
                marker: {
                  enabled: false,
                  radius: 0,
                  style: { display: 'none' },
                },
                states: {
                  hover: {
                    lineWidth: 0,
                    halo: {
                      size: 0,
                    },
                    marker: {
                      states: {
                        hover: {
                          color: 'red',
                          enabled: false,
                        },
                      },
                      enabled: false,
                      radius: 0,
                      style: { display: 'none' },
                      fillColor: 'transparent',
                    },
                  },
                },
              }

          return { ...commonProperties, ...optionalProperties }
        }),
        legend: {
          align: 'left',
        },
        chart: {
          marginTop: showExportMenu ? 50 : 16,
        },
      }}
      CustomTooltip={formatterContext => (
        <InsightsPortalKpiComparisonTooltip
          comparisonData={comparisonData}
          primaryData={primaryData}
          metricFormatter={metricFormatter}
          formatterContext={formatterContext}
          selectedMetricName={metricName}
          benchmarkData={benchmarkData}
        />
      )}
    />
  )
}
